import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { styled } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useConfirmDialog, ConfirmResponse } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Guid from "_shared/guids/guid";
import { type MRT_Row as Row } from "material-react-table";
import { TableRow } from "permissions/_views/types/permission-types";
import { JSX } from "react";

const Actions = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
}));

const EditContainer = styled("div")<{ creating: boolean }>(({ theme, creating }) => ({
  display: "flex",
  flexDirection: creating ? "column-reverse" : "row",
  gap: theme.spacing(1),
}));

export type ManageTemplateRowActionsProps = {
  templateId: Guid;
  isCreating: boolean;
  editingRow: Row<TableRow> | null;
  isLoading: boolean;
  canSave: boolean;
  onEdit(): void;
  onCancel(): void;
  onSave(): void;
  onDelete(templateId: Guid): void;
};

export default function ManageTemplateRowActions(props: Readonly<ManageTemplateRowActionsProps>): JSX.Element {
  const { templateId, editingRow, isLoading, canSave, onEdit, onCancel, onSave, onDelete } = props;

  const confirm = useConfirmDialog();

  async function handleDelete() {
    if (templateId.isEqualTo(Guid.EMPTY)) return;

    const response = await confirm({
      title: "Delete Template",
      message: "Are you sure you want to delete this template?",
    });

    if (response === ConfirmResponse.Cancel) return;

    onDelete(templateId);
  }

  const isCreating: boolean = props.isCreating;
  const isCreatingRow: boolean = templateId?.isEqualTo(Guid.EMPTY);
  const isEditing: boolean = !!editingRow;
  const isEditingRow: boolean = editingRow?.original.id.isEqualTo(templateId) ?? false;

  return (
    <Actions>
      {isCreatingRow || isEditingRow ? (
        <EditContainer creating={isCreatingRow}>
          <Button color="inherit" startIcon={<CancelIcon />} disabled={isLoading} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={!canSave || isLoading}
            onClick={onSave}
          >
            Save
          </Button>
        </EditContainer>
      ) : (
        <>
          <Tooltip title="Edit Template" disableInteractive>
            <span>
              <IconButton
                color="primary"
                disabled={(isEditing && !isEditingRow) || (isCreating && !isCreatingRow)}
                onClick={onEdit}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete Template" disableInteractive>
            <span>
              <IconButton
                color="error"
                disabled={(isEditing && !isEditingRow) || (isCreating && !isCreatingRow)}
                onClick={handleDelete}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Actions>
  );
}
